import { type MutableRefObject } from 'react'

import { F0Box, type F0FormRef } from '@factorialco/f0-react'
import { Tabs } from '@factorialco/f0-react/dist/experimental'

import type {
  PoFieldDefinitionDetailFragment,
  ProcurementValueObjectsPurchaseOrderStatusEnum,
  PurchaseOrderDetailFieldsFragment,
} from 'generated/resources/graphql'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import Attachments from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetailPanel/Attachments'

import DetailsTab from './DetailsTab'
import HeaderBanner from './HeaderBanner'
import type { LineItemDraft } from './useEditPurchaseOrder'

export interface EditCallbacks {
  replaceLineItems: (items: LineItemDraft[]) => void
  setLineItemFieldValue: (lineItemIndex: number, fieldDefinitionId: string, value: string) => void
  addLineItem: (columnDefinitions: PoFieldDefinitionDetailFragment[]) => void
  removeLineItem: (lineItemIndex: number) => void
}

interface Props {
  purchaseOrder: PurchaseOrderDetailFieldsFragment
  currentTab: string
  onTabChange: (tabId: string) => void
  isEditing?: boolean
  formRef?: MutableRefObject<F0FormRef | null>
  lineItemsRef?: MutableRefObject<LineItemDraft[]>
  lineItems?: LineItemDraft[]
  editCallbacks?: EditCallbacks
  onEditSuccess?: () => void
  targetStatus?: ProcurementValueObjectsPurchaseOrderStatusEnum
}

const PurchaseOrderDetail = ({
  purchaseOrder,
  currentTab,
  onTabChange,
  isEditing = false,
  formRef,
  lineItemsRef,
  lineItems,
  editCallbacks,
  onEditSuccess,
  targetStatus,
}: Props) => {
  const {
    access: { locale },
    company,
  } = useCurrentSession()

  return (
    <ErrorBoundary>
      <HeaderBanner purchaseOrder={purchaseOrder} locale={locale} />
      <F0Box paddingTop="xs">
        <Tabs
          activeTabId={currentTab}
          tabs={[
            {
              id: 'details',
              label: i18n.t('procurement.purchase_orders.detail.tabs.details'),
              onClick: () => onTabChange('details'),
            },
            {
              id: 'attachments',
              label: i18n.t('procurement.purchase_orders.detail.tabs.invoices'),
              onClick: () => onTabChange('attachments'),
            },
          ]}
        />
      </F0Box>
      {currentTab === 'details' && (
        <DetailsTab
          purchaseOrder={purchaseOrder}
          locale={locale}
          companyId={company.id}
          isEditing={isEditing}
          formRef={formRef}
          lineItemsRef={lineItemsRef}
          lineItems={lineItems}
          editCallbacks={editCallbacks}
          onEditSuccess={onEditSuccess}
          targetStatus={targetStatus}
        />
      )}
      {currentTab === 'attachments' && (
        <Attachments
          purchaseOrderId={purchaseOrder.id}
          vendorId={purchaseOrder?.vendor?.id ?? undefined}
        />
      )}
    </ErrorBoundary>
  )
}

export default PurchaseOrderDetail
